import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
  Switch,
  Link,
  Collapse,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import { Voyageurs } from '../models/Voyageurs';
import { API_BASE_URL } from '../config/api';
interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormChineECard: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres passés via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États issus des paramètres ou initialisés à des valeurs par défaut
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations, setInformations] = useState<string>(state?.informations || '');

  // Champs de base
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');

  // Champs spécifiques au formulaire e-Card Chine
  const [moyenTransport, setMoyenTransport] = useState<string>('');
  const [numeroVol, setNumeroVol] = useState<string>('');
  const [villeEntree, setVilleEntree] = useState<string>('');
  const [pointEntree, setPointEntree] = useState<string>('');
  const [butVoyage, setButVoyage] = useState<string>('');
  const [dateEntreeChine, setDateEntreeChine] = useState<string>('');
  const [adresseHotel, setAdresseHotel] = useState<string>('');

  // Visa Chine en cours de validité
  const [visaChineValide, setVisaChineValide] = useState<string>('');
  const [copieVisaChine, setCopieVisaChine] = useState<File | null>(null);

  // Champs liés aux voyageurs
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [showConditions, setShowConditions] = useState<boolean>(false);
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFees] = useState<boolean>(false);
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Options pour les combos
  const moyensTransport = ['Avion', 'Bateau', 'Train', 'Voiture', 'Piéton'];
  const butsVoyage = ['Affaires/Conférences', 'Tourisme', 'Travail (expatriation)', 'Études'];

  // Calcul du total
  const total = prixconsulat + prixintervention;

  // Validation du formulaire
  const validateForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);
    const entreeChineDate = parseDate(dateEntreeChine);

    const errors: string[] = [];

    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push(tp('chineECard.errors.departureDateInvalid'));
      }
    } else {
      errors.push(tp('chineECard.errors.departureDateRequired'));
    }

    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push(tp('chineECard.errors.returnDateInvalid'));
      }
    } else if (dateSortie && !sortieDate) {
      errors.push(tp('chineECard.errors.returnDateRequired'));
    }

    if (dateEntreeChine && entreeChineDate) {
      if (isNaN(entreeChineDate.getTime()) || entreeChineDate <= today) {
        errors.push(tp('chineECard.errors.dateEntreeChineInvalid'));
      }
    }

    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));
    if (!dateDepart.trim()) errors.push(t('common.validation.departureDate'));
    if (!dateSortie.trim()) errors.push(t('common.validation.returnDate'));
    if (!moyenTransport.trim()) errors.push(tp('chineECard.validation.moyenTransport'));
    if (moyenTransport === 'Avion' && !numeroVol.trim()) errors.push(tp('chineECard.validation.numeroVol'));
    if (!villeEntree.trim()) errors.push(tp('chineECard.validation.villeEntree'));
    if (!pointEntree.trim()) errors.push(tp('chineECard.validation.pointEntree'));
    if (!butVoyage.trim()) errors.push(tp('chineECard.validation.butVoyage'));
    if (!dateEntreeChine.trim()) errors.push(tp('chineECard.validation.dateEntreeChine'));
    if (!adresseHotel.trim()) errors.push(tp('chineECard.validation.adresseHotel'));
    if (!visaChineValide) errors.push(tp('chineECard.validation.visaChineValide'));
    if (visaChineValide === 'oui' && !copieVisaChine) errors.push(tp('chineECard.validation.copieVisaChine'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
  }, [dateDepart, dateSortie, sliderValue, selectedVoyageur, moyenTransport, numeroVol, villeEntree, pointEntree, butVoyage, dateEntreeChine, adresseHotel, visaChineValide, copieVisaChine]);

  // Récupération de la liste des voyageurs depuis le localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Empêcher la fermeture de l'onglet sans confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Bouton retour
  const handleBack = () => {
    if (
      window.confirm(t('common.confirmation.unsavedChanges'))
    ) {
      navigate(-1);
    }
  };

  // Fonction pour formatter la date en ajoutant automatiquement des slashs (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Soumission du formulaire via axios et redirection après succès
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }
      const etatFormulaire = champsNonRemplis.length === 0;
      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateSortie', dateSortie);
      formData.append('validite', validite);
      formData.append('assuranceVisa', 'non');
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));
      formData.append('moyenTransport', moyenTransport);
      formData.append('numeroVol', moyenTransport === 'Avion' ? numeroVol : '');
      formData.append('villeEntree', villeEntree);
      formData.append('pointEntree', pointEntree);
      formData.append('butVoyage', butVoyage);
      formData.append('dateEntreeChine', dateEntreeChine);
      formData.append('adresseHotel', adresseHotel);
      formData.append('visaChineValide', visaChineValide);
      if (visaChineValide === 'oui' && copieVisaChine) {
        formData.append('copieVisaChine', copieVisaChine, copieVisaChine.name);
      }

      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error("Erreur lors de la validation de la demande:", error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (réinitialiser le formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setSliderValue(false);
    setMoyenTransport('');
    setNumeroVol('');
    setVilleEntree('');
    setPointEntree('');
    setButVoyage('');
    setDateEntreeChine('');
    setAdresseHotel('');
    setVisaChineValide('');
    setCopieVisaChine(null);
  };

  // Fonction vide si bouton désactivé
  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate autoComplete="off" sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          {/* En-tête du formulaire */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box component="img" src={urlFlag} alt={t('common.alt.flag')} sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }} />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur (si non en mode édition) */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(v => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? t('common.selectTravelerWithPassport', { passport: v.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          {/* Saisie des dates */}
          <TextField
            label={t('common.departureDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.label')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Champs spécifiques e-Card Chine */}
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="moyen-transport-label">{tp('chineECard.fields.moyenTransport.label')}</InputLabel>
            <Select
              labelId="moyen-transport-label"
              id="moyen-transport-select"
              value={moyenTransport}
              label={tp('chineECard.fields.moyenTransport.label')}
              onChange={(e: SelectChangeEvent) => setMoyenTransport(e.target.value)}
            >
              {moyensTransport.map((transport) => {
                const transportKeyMap: Record<string, string> = {
                  'Avion': 'avion',
                  'Bateau': 'bateau',
                  'Train': 'train',
                  'Voiture': 'voiture',
                  'Piéton': 'pieton',
                };
                return (
                  <MenuItem key={transport} value={transport}>
                    {tp(`chineECard.moyenTransportOptions.${transportKeyMap[transport]}`)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Numéro du vol (affiché uniquement si Avion est sélectionné) */}
          {moyenTransport === 'Avion' && (
            <TextField
              label={tp('chineECard.fields.numeroVol.label')}
              placeholder={tp('chineECard.fields.numeroVol.placeholder')}
              value={numeroVol}
              onChange={(e) => setNumeroVol(e.target.value)}
              fullWidth
              margin="normal"
            />
          )}

          <TextField
            label={tp('chineECard.fields.villeEntree.label')}
            placeholder={tp('chineECard.fields.villeEntree.placeholder')}
            value={villeEntree}
            onChange={(e) => setVilleEntree(e.target.value)}
            fullWidth
            margin="normal"
          />

          <TextField
            label={tp('chineECard.fields.pointEntree.label')}
            placeholder={tp('chineECard.fields.pointEntree.placeholder')}
            value={pointEntree}
            onChange={(e) => setPointEntree(e.target.value)}
            fullWidth
            margin="normal"
          />

          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="but-voyage-label">{tp('chineECard.fields.butVoyage.label')}</InputLabel>
            <Select
              labelId="but-voyage-label"
              id="but-voyage-select"
              value={butVoyage}
              label={tp('chineECard.fields.butVoyage.label')}
              onChange={(e: SelectChangeEvent) => setButVoyage(e.target.value)}
            >
              {butsVoyage.map((but) => {
                const butKeyMap: Record<string, string> = {
                  'Affaires/Conférences': 'affairesConferences',
                  'Tourisme': 'tourisme',
                  'Travail (expatriation)': 'travailExpatriation',
                  'Études': 'etudes',
                };
                return (
                  <MenuItem key={but} value={but}>
                    {tp(`chineECard.butVoyageOptions.${butKeyMap[but]}`)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            label={tp('chineECard.fields.dateEntreeChine.label')}
            placeholder={tp('chineECard.fields.dateEntreeChine.placeholder')}
            value={dateEntreeChine}
            onChange={(e) => setDateEntreeChine(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          <TextField
            label={tp('chineECard.fields.adresseHotel.label')}
            placeholder={tp('chineECard.fields.adresseHotel.placeholder')}
            value={adresseHotel}
            onChange={(e) => setAdresseHotel(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />

          {/* Visa Chine en cours de validité */}
          <Box sx={{ my: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              {tp('chineECard.visaChine.sectionTitle')}
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel id="visa-chine-valide-label">
                {tp('chineECard.visaChine.questionLabel')}
              </InputLabel>
              <Select
                labelId="visa-chine-valide-label"
                value={visaChineValide}
                label={tp('chineECard.visaChine.questionLabel')}
                onChange={(e: SelectChangeEvent) => setVisaChineValide(e.target.value)}
              >
                <MenuItem value="oui">{tp('chineECard.visaChine.oui')}</MenuItem>
                <MenuItem value="non">{tp('chineECard.visaChine.non')}</MenuItem>
              </Select>
            </FormControl>
            {visaChineValide === 'oui' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {tp('chineECard.visaChine.uploadLabel')}
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setCopieVisaChine(e.target.files?.[0] || null)}
                />
                {copieVisaChine && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'green' }}>
                    ✓ {copieVisaChine.name}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Section Conditions Générales de Vente */}
          <Box sx={{ my: 3, p: 2 }}>
            <Collapse in={showConditions}>
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                  backgroundColor: '#f9f9f9',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {t('insurance.cgu.title')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ textAlign: 'justify', whiteSpace: 'pre-line', mb: 2 }}
                >
                  {t('insurance.cgu.text')}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setShowConditions(false)}
                  sx={{ display: 'block', mx: 'auto' }}
                  type="button"
                >
                  {t('insurance.cgu.close')}
                </Button>
              </Box>
            </Collapse>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {tp('chineECard.cgu.acceptancePrefix')}
                <Link
                  component="button"
                  type="button"
                  variant="body1"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setShowConditions(true);
                  }}
                  sx={{ textDecoration: 'underline', color: '#0645AD' }}
                >
                  {tp('chineECard.cgu.acceptanceLink')}
                </Link>
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Switch
                  checked={sliderValue}
                  onChange={(e) => setSliderValue(e.target.checked)}
                  color="primary"
                />
                <Typography variant="body1" sx={{ ml: 1 }}>
                  {tp('chineECard.cgu.yes')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Récapitulatif des frais */}
          {!hideFees && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">{tp('chineECard.fees.visa', { prix: prixconsulat })}</Typography>
              <Typography variant="body1">{tp('chineECard.fees.service', { prix: prixintervention })}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {tp('chineECard.fees.total', { total })}
              </Typography>
            </Box>
          )}

          {/* État du formulaire */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {/* Bouton de soumission */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormChineECard;
